import { useController } from "react-hook-form";
import type { Control, FieldPath } from "react-hook-form";

import type { CustomFieldInputType } from "@/api/db/connection";
import { CustomFieldValues } from "@/api/document/custom-field-values";
import { DocumentFieldLabels } from "@/lib/labels/document-fields";
import { cn } from "@/lib/utils";
import type { SignaturePreparationForm } from "@/routes/_app/pessoa/-utils/signature-preparation";

const inputPresentation: Record<
	CustomFieldInputType,
	{ inputMode: "text" | "numeric" | "decimal"; numeric: boolean }
> = {
	text: { inputMode: "text", numeric: false },
	date: { inputMode: "numeric", numeric: false },
	time: { inputMode: "numeric", numeric: false },
	currency: { inputMode: "decimal", numeric: true },
	number: { inputMode: "decimal", numeric: true },
	percentage: { inputMode: "decimal", numeric: true },
	cpf: { inputMode: "numeric", numeric: false },
	phone: { inputMode: "numeric", numeric: false },
};

export function SendTermsFieldInput({
	field,
	name,
	control,
	disabled,
}: {
	field: { fieldId: number; label: string; inputType: CustomFieldInputType };
	name: FieldPath<SignaturePreparationForm>;
	control: Control<SignaturePreparationForm>;
	disabled: boolean;
}) {
	const controlled = useController({ control, name });
	const value = typeof controlled.field.value === "string" ? controlled.field.value : "";
	const valid = CustomFieldValues.canonical(field.inputType, value) !== null;
	const invalid = controlled.fieldState.isTouched && value.trim() !== "" && !valid;
	const labels = DocumentFieldLabels.inputTypes[field.inputType];
	const presentation = inputPresentation[field.inputType];

	return (
		<label className="flex flex-col gap-1.5">
			<span className="flex items-baseline justify-between gap-2 text-xs font-medium text-foreground">
				{field.label}
				<span className="text-xs font-normal text-muted-foreground">{labels.label}</span>
			</span>

			<input
				ref={controlled.field.ref}
				name={controlled.field.name}
				type="text"
				inputMode={presentation.inputMode}
				autoComplete="off"
				data-slot="custom-field"
				data-field-id={field.fieldId}
				data-input-type={field.inputType}
				data-valid={valid}
				value={value}
				placeholder={labels.placeholder}
				disabled={disabled}
				aria-invalid={invalid}
				onBlur={controlled.field.onBlur}
				onChange={(event) => {
					controlled.field.onChange(CustomFieldValues.mask(field.inputType, event.target.value));
				}}
				className={cn(
					"control-field h-11 md:h-10",
					presentation.numeric && "text-right tabular-nums",
					invalid && "border-destructive",
				)}
			/>

			{invalid && <span className="text-xs text-destructive">{labels.invalid}</span>}
		</label>
	);
}
